import React, { useState } from 'react';
import { suggestTaxBenefits, GIFT_TAX_LIMITS } from '../../utils/educationCalculations';
import './TaxGuide.css';

const TaxGuide = ({ totalEducationCosts, hasGrandparents }) => {
  const [expandedSection, setExpandedSection] = useState(null);
  
  const taxBenefits = suggestTaxBenefits(totalEducationCosts, hasGrandparents);
  
  const toggleSection = (section) => {
    setExpandedSection(expandedSection === section ? null : section);
  };

  return (
    <div className="tax-guide">
      <h3>税制優遇制度ガイド</h3>
      <p className="guide-intro">
        教育費の準備には様々な税制優遇制度があります。
        お客様の状況に応じて最適な方法をご検討ください。
      </p>

      {/* 推奨制度 */}
      {taxBenefits.length > 0 && (
        <div className="recommended-benefits">
          <h4>🎯 お客様におすすめの制度</h4>
          {taxBenefits.map((benefit, index) => (
            <div key={index} className={`benefit-card priority-${benefit.priority}`}>
              <div className="benefit-header">
                <h5>{benefit.title}</h5>
                {benefit.priority === 1 && <span className="recommended-badge">最推奨</span>}
              </div>
              <p className="benefit-description">{benefit.description}</p>
              <div className="benefit-details">
                <span className="max-amount">
                  上限: {(benefit.maxAmount / 10000).toLocaleString()}万円
                </span>
                <span className={`risk-level ${benefit.riskLevel}`}>
                  リスク: {benefit.riskLevel === 'none' ? 'なし' : 
                          benefit.riskLevel === 'low' ? '低' : '中'}
                </span>
              </div>
              {benefit.warning && (
                <div className="benefit-warning">
                  ⚠️ {benefit.warning}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* 詳細ガイド */}
      <div className="detailed-guide">
        <h4>詳細ガイド</h4>
        
        {/* 教育費直接支払い */}
        <div className="guide-section">
          <button 
            className="section-header"
            onClick={() => toggleSection('direct')}
          >
            <span className="section-title">💡 教育費の直接支払い（最も安全）</span>
            <span className="expand-icon">{expandedSection === 'direct' ? '−' : '+'}</span>
          </button>
          
          {expandedSection === 'direct' && (
            <div className="section-content">
              <div className="highlight-box recommended">
                <h5>🌟 最も推奨される方法</h5>
                <p>
                  祖父母が学校に直接学費を支払う場合、金額に関係なく贈与税はかかりません。
                  これが最もシンプルで安全な方法です。
                </p>
              </div>
              
              <div className="details-grid">
                <div className="detail-item">
                  <h6>対象</h6>
                  <ul>
                    <li>小学校・中学校・高校の学費</li>
                    <li>大学の授業料・入学金</li>
                    <li>制服代・教科書代</li>
                  </ul>
                </div>
                
                <div className="detail-item">
                  <h6>メリット</h6>
                  <ul>
                    <li>金額制限なし</li>
                    <li>手続き不要</li>
                    <li>贈与税完全非課税</li>
                    <li>年齢制限なし</li>
                  </ul>
                </div>
                
                <div className="detail-item">
                  <h6>注意点</h6>
                  <ul>
                    <li>学校への直接支払いが必要</li>
                    <li>一度親に渡すと贈与税の対象</li>
                    <li>領収書の保管推奨</li>
                  </ul>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* 教育資金一括贈与 */}
        <div className="guide-section">
          <button 
            className="section-header"
            onClick={() => toggleSection('bulk')}
          >
            <span className="section-title">📋 教育資金一括贈与の非課税制度</span>
            <span className="expand-icon">{expandedSection === 'bulk' ? '−' : '+'}</span>
          </button>
          
          {expandedSection === 'bulk' && (
            <div className="section-content">
              <div className="highlight-box warning">
                <h5>⚠️ 重要な制限があります</h5>
                <p>
                  最大1,500万円まで非課税ですが、30歳までに使い切る必要があります。
                  <strong>1,500万円を超える場合は税金がかかります。</strong>
                </p>
              </div>
              
              <div className="limits-breakdown">
                <div className="limit-item">
                  <span className="limit-category">学校等への支払い</span>
                  <span className="limit-amount">1,500万円</span>
                </div>
                <div className="limit-item">
                  <span className="limit-category">学習塾・習い事</span>
                  <span className="limit-amount">500万円</span>
                </div>
              </div>
              
              <div className="procedure-info">
                <h6>手続きが必要</h6>
                <ul>
                  <li>金融機関での専用口座開設</li>
                  <li>教育資金管理契約の締結</li>
                  <li>領収書の提出義務</li>
                  <li>30歳時点での残額は贈与税課税</li>
                </ul>
              </div>
            </div>
          )}
        </div>

        {/* 暦年贈与 */}
        <div className="guide-section">
          <button 
            className="section-header"
            onClick={() => toggleSection('annual')}
          >
            <span className="section-title">📅 暦年贈与（毎年110万円まで）</span>
            <span className="expand-icon">{expandedSection === 'annual' ? '−' : '+'}</span>
          </button>
          
          {expandedSection === 'annual' && (
            <div className="section-content">
              <div className="highlight-box info">
                <h5>💡 継続的な支援に最適</h5>
                <p>
                  毎年110万円まで非課税で贈与できます。
                  他の制度と併用可能で、長期的な教育資金準備に適しています。
                </p>
              </div>
              
              <div className="annual-calculation">
                <h6>18年間での累積効果</h6>
                <div className="calculation-result">
                  <span className="calc-label">年間110万円 × 18年 =</span>
                  <span className="calc-amount">1,980万円</span>
                </div>
                <p className="calc-note">
                  教育費総額（{(totalEducationCosts / 10000).toFixed(1)}万円）を
                  十分にカバーできます
                </p>
              </div>
              
              <div className="tips">
                <h6>活用のコツ</h6>
                <ul>
                  <li>毎年確実に贈与を実行</li>
                  <li>贈与契約書の作成推奨</li>
                  <li>受贈者の口座で管理</li>
                  <li>他の制度との併用可能</li>
                </ul>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* 簡単比較表 */}
      <div className="comparison-table">
        <h4>制度比較表</h4>
        <table>
          <thead>
            <tr>
              <th>制度</th>
              <th>上限額</th>
              <th>手続き</th>
              <th>制限</th>
              <th>おすすめ度</th>
            </tr>
          </thead>
          <tbody>
            <tr className="recommended-row">
              <td>直接支払い</td>
              <td>制限なし</td>
              <td>不要</td>
              <td>学校への直接支払い</td>
              <td>⭐⭐⭐⭐⭐</td>
            </tr>
            <tr>
              <td>一括贈与</td>
              <td>1,500万円</td>
              <td>必要</td>
              <td>30歳まで使い切り</td>
              <td>⭐⭐⭐</td>
            </tr>
            <tr>
              <td>暦年贈与</td>
              <td>110万円/年</td>
              <td>簡単</td>
              <td>なし</td>
              <td>⭐⭐⭐⭐</td>
            </tr>
          </tbody>
        </table>
      </div>

      {/* 免責事項 */}
      <div className="disclaimer">
        <h5>⚠️ 免責事項</h5>
        <p>
          税制に関する情報は2024年時点のものです。
          実際の適用にあたっては、税理士や税務署にご相談ください。
          税制改正により内容が変更される場合があります。
        </p>
      </div>
    </div>
  );
};

export default TaxGuide;
